import { Epic, ofType } from 'redux-observable';
import { of, concat, from } from 'rxjs';
import { mergeMap, map, catchError } from 'rxjs/operators';
import { push } from 'connected-react-router';
import { IAction, IRootState, IEpicDependencies } from '../rootState';
import { actions, ActionType } from './actions';
import { generalState } from '../general';
import { GENERAL } from '../../../constants';
import { ToastType } from '../../models/general';
import { handleError, handleToastError } from '../core/operators';
import { statisticsState } from '../statistics';
import { coreState } from '../core';
import { authState } from '../auth';
import { GeneralModel } from 'modules/models';
const config = process.env;

export const fetchClientStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_CLIENT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_CLIENT, true)),
        deps.apiService.getClient(payload.id).pipe(map(res => actions.fetchClientListSuccess([res], 1))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_CLIENT, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_CLIENT))
    )
  );

export const fetchSelfCompanyStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_SELF_COMPANY_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_SELF_COMPANY, true)),
        deps.apiService.getClient(state$.value.auth.currentCompanyId).pipe(map(res => actions.fetchSelfClientSuccess(res))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_SELF_COMPANY, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_SELF_COMPANY))
    )
  );

export const fetchDraftClientStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_DRAFT_CLIENT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_CLIENT, true)),
        deps.apiService.getDraftClient(payload.id).pipe(mergeMap(res => [actions.fetchClientListSuccess([res], 1)])),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_CLIENT, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_CLIENT))
    )
  );

export const fetchClientHierarchyStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_CLIENT_HIERARCHY_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_CLIENT_HIERARCHY, true)),
        deps.apiService.getClientHierarchy(payload.id, payload.query).pipe(map(res => actions.fetchClientHierarchySuccess(res))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_CLIENT_HIERARCHY, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_CLIENT_HIERARCHY))
    )
  );

export const updateClientHierarchyStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.UPDATE_CLIENT_HIERARCHY_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_CLIENT_HIERARCHY, true)),
        deps.apiService
          .updateClientHierarchy(payload.id, payload.hierarchy)
          .pipe(
            mergeMap(res =>
              res && res.id
                ? [actions.clearClientHierarchy(), actions.fetchClientHierarchyStart(res.id, { includeImmediateParent: true })]
                : [actions.clearClientHierarchy()]
            )
          ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_CLIENT_HIERARCHY, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.UPDATE_CLIENT_HIERARCHY))
    )
  );

export const fetchUserClientsStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_USER_CLIENTS_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.AUTH_LOGIN, true)),
        deps.apiService.getUserClientList(payload.id).pipe(
          mergeMap(res => {
            const actionsCollection: IAction[] = [];
            if (res && res.length > 1) {
              actionsCollection.push(actions.fetchUserClientsSuccess(res));
              actionsCollection.push(generalState.actions.setLoading(GENERAL.LOADING_KEY.AUTH_LOGIN, false));
            } else {
              actionsCollection.push(authState.actions.setClientId(null));
              if (res?.length === 1 && state$.value.auth.originalCompanyId && state$.value.auth.originalCompanyId !== res?.[0]?.id) {
                actionsCollection.push(authState.actions.initiateAuth(state$.value.auth.userName, state$.value.auth.clientId, res?.[0]?.id));
                actionsCollection.push(generalState.actions.setLoading(GENERAL.LOADING_KEY.AUTH_LOGIN, false));
              } else {
                actionsCollection.push(authState.actions.recoverSessionStart());
                actionsCollection.push(
                  generalState.actions.setLoading(
                    GENERAL.LOADING_KEY.AUTH_LOGIN,
                    false,
                    true,
                    'Unable to recognize user account. Please contact customer support.'
                  )
                );
              }
            }
            return concat(from(actionsCollection));
          })
        )
        // of(generalState.actions.setLoading(GENERAL.LOADING_KEY.AUTH_LOGIN, false))
      ).pipe(catchError(error => of(coreState.actions.epicError(error), generalState.actions.setLoading(GENERAL.LOADING_KEY.AUTH_LOGIN, false, true, error))))
    )
  );

export const unmarkBranchStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.UNMARK_BRANCH_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UNMARK_BRANCH, true)),
        deps.apiService.updateClientHierarchy(payload.id, payload.hierarchy).pipe(map(() => actions.clearClientHierarchy())),
        of(push(`/clients/detail/${payload.id}/information`, { success: true })),
        of(generalState.actions.addToastStart(`Branched is unmarked and company Archived successfully!`, ToastType.SUCCESS)),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UNMARK_BRANCH, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.UNMARK_BRANCH))
    )
  );

// export const archiveClientStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
//   action$.pipe(
//     ofType(ActionType.ARCHIVE_CLIENT_START),
//     mergeMap(({ payload }) =>
//       concat(
//         of(generalState.actions.setLoading(GENERAL.LOADING_KEY.ARCHIVE_CLIENT, true)),
//         deps.apiService.archiveClient(payload.id).pipe(map(() => actions.archiveClientSuccess(payload.id))),
//         of(push(`/clients/detail/${payload.id}/information`)),
//         of(generalState.actions.addToastStart(`${state$.value.client.clientMap[payload.id].name} archived successfully!`, ToastType.SUCCESS)),
//         of(generalState.actions.setLoading(GENERAL.LOADING_KEY.ARCHIVE_CLIENT, false))
//       ).pipe(handleToastError(GENERAL.LOADING_KEY.ARCHIVE_CLIENT, ['COMPANY_IS_ASSIGNED_TO_ACTIVE_PROJECT']))
//     )
//   );

export const fetchClientBranchesStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_CLIENT_BRANCHES_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_CLIENT_BRANCHES, true)),
        deps.apiService.getClientHierarchy(payload.id, payload.query).pipe(map(res => actions.fetchClientBranchesSuccess(payload.id, res))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_CLIENT_BRANCHES, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_CLIENT_BRANCHES))
    )
  );

export const inviteCompanyAsABranchStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.INVITE_COMPANY_AS_BRANCH_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.INVITE_COMPANY_AS_BRANCH, true)),
        deps.apiService.inviteCompanyAsABranch(payload.id, payload.hierarchy).pipe(mergeMap(res => [])),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.INVITE_COMPANY_AS_BRANCH, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.INVITE_COMPANY_AS_BRANCH))
    )
  );

export const fetchOnboardingClientStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_ONBOARDING_CLIENT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_ONBOARDING_CLIENT, true)),
        deps.apiService.getOnboardingClient(payload.id).pipe(map(res => actions.fetchOnboardingClientListSuccess([res], 1))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_ONBOARDING_CLIENT, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_ONBOARDING_CLIENT))
    )
  );

export const fetchClientListStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_CLIENT_LIST_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_CLIENT_LIST, true)),
        deps.apiService
          .getClientList(payload.query)
          .pipe(map(res => actions.fetchClientListSuccess(res.items, res.totalResults, typeof payload?.query?.companyProjectStatus === 'number'))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_CLIENT_LIST, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_CLIENT_LIST))
    )
  );

export const fetchUserClientListStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_USER_CLIENT_LIST_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_CLIENT_LIST, true)),
        deps.apiService.getProjectClientList(payload.id, payload.query).pipe(map(res => actions.fetchClientListSuccess(res.items, res.totalResults))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_CLIENT_LIST, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_CLIENT_LIST))
    )
  );

export const fetchSubContractorListStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_SUBCONTRACTOR_LIST_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_CLIENT_SUBCONTRACTOR_LIST, true)),
        deps.apiService.getClientList(payload.query).pipe(map(res => generalState.actions.setModalMap(res.items, res.totalResults))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_CLIENT_SUBCONTRACTOR_LIST, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_CLIENT_SUBCONTRACTOR_LIST))
    )
  );

export const fetchClientSummaryStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_CLIENT_SUMMARY_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_CLIENT_SUMMARY, true)),
        deps.apiService.getClientSummary(payload.id).pipe(map(res => actions.updateClientSuccess(res, payload.projectId))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_CLIENT_SUMMARY, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_CLIENT_SUMMARY))
    )
  );

export const fetchClientDraftSummaryStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_CLIENT_DRAFT_SUMMARY_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_CLIENT_SUMMARY, true)),
        deps.apiService.getDraftClientSummary(payload.id).pipe(map(res => actions.updateClientSuccess(res, payload.projectId))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_CLIENT_SUMMARY, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_CLIENT_SUMMARY))
    )
  );

export const fetchMwbeTypesStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_MWBE_START),
    mergeMap(() =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_MBE_WBE, true)),
        deps.apiService.getMwbeTypes().pipe(map(res => actions.fetchMWbeListSuccess(res))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_MBE_WBE, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_MBE_WBE))
    )
  );

export const fetchTradesStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_TRADES_START),
    mergeMap(() =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_TRADES, true)),
        deps.apiService.getTrades().pipe(map(res => actions.fetchTradesSuccess(res))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_TRADES, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_TRADES))
    )
  );

export const fetchProjectClientListStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_PROJECT_CLIENT_LIST_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_PROJECT_CLIENT_LIST, true)),
        deps.apiService
          .getProjectClientList(payload.id, payload.query)
          .pipe(map(res => actions.fetchProjectClientListSuccess(payload.id, res.items, res.totalResults))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_PROJECT_CLIENT_LIST, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_PROJECT_CLIENT_LIST))
    )
  );

export const saveClientStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.SAVE_CLIENT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.SAVE_CLIENT, true)),
        deps.apiService
          .saveClient(payload.client)
          .pipe(
            mergeMap(res =>
              [
                actions.fetchClientListSuccess([res], 1),
                payload.redirect && push(`/clients/wizard/${res.id}/${payload.step.key}`, { success: true }),
                payload.uiRelationId && generalState.actions.setRelationUiId(payload.uiRelationId, { clientId: res.id }),
              ].filter(Boolean)
            )
          ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.SAVE_CLIENT, false))
      ).pipe(
        handleError(GENERAL.LOADING_KEY.SAVE_CLIENT, error => [
          payload.uiRelationId &&
            error.response.errors.name &&
            generalState.actions.setRelationUiId(payload.uiRelationId, { error: 'Company Name is already in use.' }),
        ])
      )
    )
  );

export const inviteClientStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.INVITE_DRAFT_CLIENT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.INVITE_DRAFT_CLIENT, true)),
        deps.apiService.inviteDraftClient(payload.client).pipe(
          mergeMap(res =>
            [
              payload.hasInviteAsBranch
                ? actions.inviteCompanyAsABranchStart(res.id, {
                    companyId: res.id,
                    companyType: payload.client.companyType,
                    reportsTo: payload.client.reportsTo,
                    relationshipName: payload.client.relationshipName,
                    branches: [],
                  })
                : null,
              actions.inviteDraftClientSuccess(res),
              push(`/projects`, { success: true }),
            ].filter(Boolean)
          )
        ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.INVITE_DRAFT_CLIENT, false)),
        of(generalState.actions.addToastStart(`Company invited successfully! ${payload.client?.name} is in Invitation Sent now.`, ToastType.SUCCESS))
      ).pipe(handleError(GENERAL.LOADING_KEY.INVITE_DRAFT_CLIENT))
    )
  );

export const updateDraftClientStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.UPDATE_DRAFT_CLIENT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.SAVE_CLIENT, true)),
        deps.apiService
          .updateDraftClient(payload.client)
          .pipe(map(res => actions.updateDraftClientSuccess({ ...res, reportsTo: payload.client.reportsTo, reportsToName: payload.client.reportsToName }))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.SAVE_CLIENT, false))
      ).pipe(handleToastError(GENERAL.LOADING_KEY.SAVE_CLIENT))
    )
  );

export const updateClientStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.UPDATE_CLIENT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_CLIENT, true)),
        deps.apiService
          .updateClient(payload.client)
          .pipe(map(res => actions.updateClientSuccess({ ...res, reportsTo: payload.client.reportsTo, reportsToName: payload.client.reportsToName }))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_CLIENT, false)),
        of(generalState.actions.addToastStart('Changes saved successfully!', ToastType.SUCCESS))
      ).pipe(
        handleToastError(GENERAL.LOADING_KEY.UPDATE_CLIENT, [], res => {
          const errors = [];
          Object.keys(res.errors).forEach(key => {
            if (key.includes('billingAddress') && !errors.includes('Billing Address is required')) errors.push('Billing Address is required');
            if (key.includes('mailingAddress') && !errors.includes('Mailing Address is required')) errors.push('Mailing Address is required');
            if (!key.includes('billingAddress') && !key.includes('mailingAddress') && key) errors.push(res.errors[key]);
          });
          return errors.map(err => generalState.actions.addToastStart(err, ToastType.ERROR));
        })
      )
    )
  );

export const updateOnboardingClientStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.UPDATE_ONBOARDING_CLIENT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_ONBAORDING_CLIENT, true)),
        deps.apiService
          .updateOnboardingClient(payload.client)
          .pipe(
            map(res => actions.updateOnboardingClientSuccess({ ...res, reportsTo: payload.client.reportsTo, reportsToName: payload.client.reportsToName }))
          ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_ONBAORDING_CLIENT, false)),
        of(generalState.actions.addToastStart('Changes saved successfully!', ToastType.SUCCESS))
      ).pipe(handleToastError(GENERAL.LOADING_KEY.UPDATE_ONBAORDING_CLIENT))
    )
  );

export const archiveClientStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.ARCHIVE_CLIENT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.ARCHIVE_CLIENT, true)),
        deps.apiService.archiveClient(payload.id).pipe(map(() => actions.archiveClientSuccess(payload.id))),
        of(push(`/clients/detail/${payload.id}/information`)),
        of(generalState.actions.addToastStart(`${state$.value.client.clientMap[payload.id].name} archived successfully!`, ToastType.SUCCESS)),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.ARCHIVE_CLIENT, false))
      ).pipe(handleToastError(GENERAL.LOADING_KEY.ARCHIVE_CLIENT, ['COMPANY_IS_ASSIGNED_TO_ACTIVE_PROJECT']))
    )
  );

export const unarchiveClientStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.UNARCHIVE_CLIENT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UNARCHIVE_CLIENT, true)),
        deps.apiService.unarchiveClient(payload.id).pipe(map(() => actions.unarchiveClientSuccess(payload.id))),
        of(
          push(`/clients/detail/${payload.id}/information`),
          generalState.actions.addToastStart(`${state$.value.client.clientMap[payload.id].name} unarchived successfully!`, ToastType.SUCCESS)
        ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UNARCHIVE_CLIENT, false))
      ).pipe(handleToastError(GENERAL.LOADING_KEY.UNARCHIVE_CLIENT, []))
    )
  );

export const sendApproveClientStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.SEND_FOR_APPROVE_CLIENT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.SEND_APPROVE_CLIENT, true)),
        deps.apiService
          .sendApproveClient(payload.id)
          .pipe(
            mergeMap(() =>
              of(
                push('/clients?filter="pending-approval"'),
                generalState.actions.addToastStart(
                  `Company created successfully! ${state$.value.client.clientMap[payload.id].name} is pending approval now`,
                  ToastType.SUCCESS
                )
              )
            )
          ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.SEND_APPROVE_CLIENT, false))
      ).pipe(handleToastError(GENERAL.LOADING_KEY.SEND_APPROVE_CLIENT, []))
    )
  );

export const sendApproveOnboardingClientStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.SEND_FOR_APPROVE_ONBOARDING_CLIENT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.SEND_ONBOARDING_APPROVE_CLIENT, true)),
        deps.apiService
          .sendApproveOnboardingClient(payload.id)
          .pipe(
            mergeMap(() =>
              of(
                generalState.actions.addToastStart(
                  `Company created successfully! ${state$.value.client.onboardingClientMap[payload.id].name} is ${
                    state$.value.client.onboardingClientMap[payload.id]?.companyProject?.id ? 'active' : 'pending approval'
                  } now`,
                  ToastType.SUCCESS
                )
              )
            )
          ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.SEND_ONBOARDING_APPROVE_CLIENT, false))
      ).pipe(handleToastError(GENERAL.LOADING_KEY.SEND_ONBOARDING_APPROVE_CLIENT, []))
    )
  );

export const approveClientStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.APPROVE_CLIENT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.APPROVE_CLIENT, true)),
        deps.apiService
          .approveClient(payload.id)
          .pipe(
            mergeMap(() =>
              of(
                push('/clients?filter="active"'),
                generalState.actions.addToastStart(
                  `Company approved successfully! ${state$.value.client.clientMap[payload.id].name} is active now`,
                  ToastType.SUCCESS
                )
              )
            )
          ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.APPROVE_CLIENT, false))
      ).pipe(handleToastError(GENERAL.LOADING_KEY.APPROVE_CLIENT, []))
    )
  );

export const approveOnboardingClientStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.APPROVE_ONBOARDING_CLIENT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.APPROVE_ONBOARDING_CLIENT, true)),
        deps.apiService
          .approveOnboardingClient(payload.id)
          .pipe(
            mergeMap(() =>
              of(
                push('/clients?filter="active"'),
                generalState.actions.addToastStart(
                  `Company approved successfully! ${state$.value.client.onboardingClientMap[payload.id].name} is active now`,
                  ToastType.SUCCESS
                )
              )
            )
          ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.APPROVE_ONBOARDING_CLIENT, false))
      ).pipe(handleToastError(GENERAL.LOADING_KEY.APPROVE_ONBOARDING_CLIENT, []))
    )
  );

export const deleteClientStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.DELETE_CLIENT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.DELETE_CLIENT, true)),
        deps.apiService
          .deleteClient(payload.id)
          .pipe(
            mergeMap(() => [
              payload.query.newPage
                ? push(`/clients?filter="${payload.query.filter}"&page=${payload.query.page - 1}&limit=${payload.query.limit}`)
                : actions.fetchClientListStart(payload.query),
              statisticsState.actions.fetchClientStatisticsStart(),
            ])
          ),
        of(generalState.actions.addToastStart(`${state$.value.client.clientMap[payload.id].name} deleted successfully!`, ToastType.SUCCESS)),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.DELETE_CLIENT, false))
      ).pipe(handleToastError(GENERAL.LOADING_KEY.DELETE_CLIENT, ['COMPANY_IS_ASSIGNED_TO_PROJECT']))
    )
  );

export const deleteOnboardingClientStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.DELETE_ONBOARDING_CLIENT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.DELETE_CLIENT, true)),
        deps.apiService
          .deleteOnboardingClient(payload.id)
          .pipe(
            mergeMap(() => [
              payload.query.newPage
                ? push(`/clients?filter="${payload.query.filter}"&page=${payload.query.page - 1}&limit=${payload.query.limit}`)
                : actions.fetchClientListStart(payload.query),
              statisticsState.actions.fetchClientStatisticsStart(),
            ])
          ),
        of(generalState.actions.addToastStart(`${state$.value.client.clientMap[payload.id].name} deleted successfully!`, ToastType.SUCCESS)),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.DELETE_CLIENT, false))
      ).pipe(handleToastError(GENERAL.LOADING_KEY.DELETE_CLIENT, ['COMPANY_IS_ASSIGNED_TO_PROJECT']))
    )
  );

export const searchClientStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.SEARCH_CLIENT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(payload.isSecond ? GENERAL.LOADING_KEY.FETCH_SEARCH_CLIENT_2 : GENERAL.LOADING_KEY.FETCH_SEARCH_CLIENT, true)),
        deps.apiService
          .searchClient(payload.query)
          .pipe(map(res => generalState.actions.setRelationUiId(payload.uiRelationId, { searchResult: res }, payload.isSecond))),
        of(generalState.actions.setLoading(payload.isSecond ? GENERAL.LOADING_KEY.FETCH_SEARCH_CLIENT_2 : GENERAL.LOADING_KEY.FETCH_SEARCH_CLIENT, false))
      ).pipe(handleError(payload.isSecond ? GENERAL.LOADING_KEY.FETCH_SEARCH_CLIENT_2 : GENERAL.LOADING_KEY.FETCH_SEARCH_CLIENT))
    )
  );

export const searchInviteClientStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_SEARCH_INVITE_CLIENT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_SEARCH_INVITE_CLIENT, true)),
        deps.apiService.getClientList(payload.query).pipe(map(res => actions.searchInviteClientSuccess(res))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_SEARCH_INVITE_CLIENT, false))
      ).pipe(
        catchError(error =>
          of(coreState.actions.epicError(error), generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_SEARCH_INVITE_CLIENT, false, true, error))
        )
      )
    )
  );

export const fetchProjectClientHirearchyListStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_PROJECT_CLIENT_HIREARCHY_LIST_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_PROJECT_CLIENT_HIREARCHY_LIST, true)),
        deps.apiService.getProjectClientHirearchyList(payload.id).pipe(map(res => actions.fetchProjectClientHirearchyListSuccess(res.items))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_PROJECT_CLIENT_HIREARCHY_LIST, false))
      ).pipe(handleToastError(GENERAL.LOADING_KEY.FETCH_PROJECT_CLIENT_HIREARCHY_LIST))
    )
  );

export const fetchProjectClientSummaryStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_PROJECT_CLIENT_SUMMARY_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_PROJECT_CLIENT_SUMMARY, true)),
        deps.apiService
          .getProjectClientSummary(payload.projectId, payload.clientId)
          .pipe(map(res => actions.fetchProjectClientSummarySuccess(payload.projectId, res))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_PROJECT_CLIENT_SUMMARY, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_PROJECT_CLIENT_SUMMARY))
    )
  );

export const updateProjectClientTaxConditionStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.UPDATE_PROJECT_CLIENT_TAX_CONDITION_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_PROJECT_CLIENT_TAX_CONDITION, true)),
        deps.apiService
          .updateClientProjectTaxCondition(payload.projectId, payload.taxCondition)
          .pipe(
            map(res => actions.updateProjectClientTaxConditionSuccess(payload.projectId, payload.taxCondition.companyId, payload.taxCondition.isTaxExempt))
          ),
        of(generalState.actions.addToastStart(`Tax Condition updated successfully!`, ToastType.SUCCESS)),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_PROJECT_CLIENT_TAX_CONDITION, false))
      ).pipe(handleToastError(GENERAL.LOADING_KEY.UPDATE_PROJECT_CLIENT_TAX_CONDITION))
    )
  );

export const updateWelcomeKitStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.UPDATE_WELCOME_KIT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_WELCOME_KIT, true)),
        deps.apiService.updateWelcomeKit(payload).pipe(map(res => generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_WELCOME_KIT, false))),
        of(generalState.actions.addToastStart(`Welcome Kit status updated successfully!`, ToastType.SUCCESS))
      ).pipe(handleToastError(GENERAL.LOADING_KEY.UPDATE_WELCOME_KIT))
    )
  );

export const fetchAdminPermissionStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_ADMIN_PERMISSION_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_ADMIN_PERMISSION, true)),
        deps.apiService.getAdminPermission(payload.id).pipe(map(res => actions.fetchAdminPermissionSuccess(res))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_ADMIN_PERMISSION, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_ADMIN_PERMISSION))
    )
  );

export const fetchProjectCompanyStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_PROJECT_COMPANY_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_PROJECT_COMPANY, true)),
        deps.apiService.getProjectCompanies(payload.companyId, payload.projectId).pipe(map(res => actions.fetchProjectCompanySuccess(res))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_PROJECT_COMPANY, false))
      ).pipe(handleToastError(GENERAL.LOADING_KEY.FETCH_PROJECT_COMPANY))
    )
  );

export const updateProjectCompanyStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.UPDATE_PROJECT_COMPANY_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_PROJECT_COMPANY, true)),
        deps.apiService.updateProjectCompanies(payload.client).pipe(map(res => actions.updateProjectCompanySuccess(res))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_PROJECT_COMPANY, false)),
        of(generalState.actions.addToastStart('Changes saved successfully!', ToastType.SUCCESS))
      ).pipe(
        catchError(error =>
          of(
            coreState.actions.epicError(error),
            generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_PROJECT_COMPANY, false, true, error),
            generalState.actions.addToastStart(error.response?.errors?.users ? error.response.errors.users[0] : error.title, ToastType.ERROR)
          )
        )
      )
    )
  );

export const fetchJointVentureCompanyUsersStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_JOINT_VENTURE_COMPANY_USERS_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_JOINT_VENTURE_COMPANY_USERS, true)),
        deps.apiService
          .searchJointVentureCompanyUsers(payload.companyId, payload.query)
          .pipe(mergeMap(res => [actions.fetchJointVentureCompanyUsersSuccess(payload.companyId, res)])),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_JOINT_VENTURE_COMPANY_USERS, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_JOINT_VENTURE_COMPANY_USERS))
    )
  );

export const assignClientProjectStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.ASSIGN_CLIENT_PROJECT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.ASSIGN_CLIENT_PROJECT, true)),
        deps.apiService.assignClientProject(payload.id, payload.list, payload.sponsorId).pipe(
          mergeMap(res => [
            actions.assignClientProjectSuccess(payload.list),
            statisticsState.actions.fetchProjectDetailStatisticsStart(payload.id),
            generalState.actions.addToastStart(`Company invited to project successfully, invitation sent!`, GeneralModel.ToastType.SUCCESS),
            // push(`/projects/detail/${payload.id}`),
          ])
        ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.ASSIGN_CLIENT_PROJECT, false))
      ).pipe(handleToastError(GENERAL.LOADING_KEY.ASSIGN_CLIENT_PROJECT, ['COMPANY_IS_ASSIGNED_TO_PROJECT', 'COMPANY_IS_NOT_ASSIGNABLE', 'sponsorId']))
    )
  );

export const fetchFailedTransactionsStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_FAILED_TRANSACTIONS_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_FAILED_TRANSACTIONS, true)),
        deps.apiService.fetchFailedTransactions(payload.companyId).pipe(mergeMap(res => [actions.fetchFailedTransactionsSuccess(res)])),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_FAILED_TRANSACTIONS, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_FAILED_TRANSACTIONS))
    )
  );

export const fetchMergeCompanySummaryStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_MERGE_COMPANY_SUMMARY_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_SUMMARY, true)),
        deps.apiService.getMergeCompanySummary(payload.id).pipe(map(res => actions.fetchMergeCompanySummarySuccess(payload.id, res))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_SUMMARY, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_SUMMARY))
    )
  );

export const fetchMergeCompanyProjectSummaryStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_MERGE_COMPANY_PROJECT_SUMMARY_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_PROJECT_SUMMARY, true)),
        deps.apiService.getMergeCompanyProjectSummary(payload.id).pipe(map(res => actions.fetchMergeCompanyProjectSummarySuccess(payload.id, res))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_PROJECT_SUMMARY, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_PROJECT_SUMMARY))
    )
  );

export const fetchMergeCompanyUserStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_MERGE_COMPANY_USERS_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_USERS, true)),
        deps.apiService.getMergeCompanyUsers(payload.id).pipe(map(res => actions.fetchMergeCompanyUserSuccess(payload.id, res))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_USERS, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_USERS))
    )
  );

export const fetchMergeCompanyBillingSummaryStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_MERGE_COMPANY_BILLING_SUMMARY_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_BILLING_SUMMARY, true)),
        deps.apiService.getMergeCompanyBillingSummary(payload.id).pipe(map(res => actions.fetchMergeCompanyBillingSummarySuccess(payload.id, res))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_BILLING_SUMMARY, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_BILLING_SUMMARY))
    )
  );

export const fetchMergeCompanyInvoicesSummaryStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_MERGE_COMPANY_INVOICES_SUMMARY_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_INVOICES_SUMMARY, true)),
        deps.apiService.getMergeCompanyInvoicesSummary(payload.id).pipe(map(res => actions.fetchMergeCompanyInvoicesSummarySuccess(payload.id, res))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_INVOICES_SUMMARY, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_INVOICES_SUMMARY))
    )
  );

export const fetchMergeCompanyReviewInvoicesSummaryStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_MERGE_COMPANY_REVIEW_INVOICES_SUMMARY_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_REVIEW_INVOICES_SUMMARY, true)),
        deps.apiService
          .getMergeCompanyInvoicesSummary(payload.id, payload.query)
          .pipe(map(res => actions.fetchMergeCompanyReviewInvoicesSummarySuccess(payload.id, res))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_REVIEW_INVOICES_SUMMARY, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_REVIEW_INVOICES_SUMMARY))
    )
  );

export const fetchMergeCompanyInvoicesStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_MERGE_COMPANY_INVOICES_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_INVOICES, true)),
        deps.apiService.getMergeCompanyInvoices(payload.id).pipe(map(res => actions.fetchMergeCompanyInvoicesSuccess(payload.id, res))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_INVOICES, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_INVOICES))
    )
  );

export const fetchMergeCompanyWorkersStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_MERGE_COMPANY_WORKERS_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_WORKERS, true)),
        deps.apiService.getMergeCompanyWorkers(payload.id).pipe(map(res => actions.fetchMergeCompanyWorkersSuccess(payload.id, res))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_WORKERS, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_MERGE_COMPANY_WORKERS))
    )
  );
export const saveMergeCompanyStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.SAVE_MERGE_COMPANY_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.SAVE_MERGE_COMPANY, true)),
        deps.apiService.saveMergeCompany(payload.mergeCompany).pipe(map(res => actions.saveMergeCompanySuccess())),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.SAVE_MERGE_COMPANY, false)),
        of(generalState.actions.addToastStart('Request to merge companies is successful!', ToastType.SUCCESS))
      ).pipe(handleToastError(GENERAL.LOADING_KEY.SAVE_MERGE_COMPANY))
    )
  );

export const fetchAllActiveClientsStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_ALL_ACTIVE_CLIENTS_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_ALL_ACTIVE_CLIENTS, true)),
        deps.apiService.getAllActiveClients(payload.query).pipe(map(res => actions.fetchAllActiveClientsSuccess(res.items || []))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_ALL_ACTIVE_CLIENTS, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_ALL_ACTIVE_CLIENTS))
    )
  );
export const fetchClientListForMultipleProjectsStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_CLIENT_LIST_FOR_MULTIPLE_PROJECTS_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_CLIENT_LIST_FOR_MULTIPLE_PROJECTS, true)),
        deps.apiService.getClientListForMultipleProjects(payload.query).pipe(map(res => actions.fetchClientListForMultipleProjectsSuccess(res.items))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_CLIENT_LIST_FOR_MULTIPLE_PROJECTS, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_CLIENT_LIST_FOR_MULTIPLE_PROJECTS))
    )
  );

export const addUnverifiedCompaniesStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.ADD_UNVERIFIED_COMPANIES_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.ADD_UNVERIFIED_COMPANIES_START, true)),
        deps.apiService.addUnverifiedCompanies(payload.client).pipe(
          mergeMap(res => {
            return [actions.addUnverifiedCompaniesSuccess(res)];
          })
        ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.ADD_UNVERIFIED_COMPANIES_START, false)),
        of(generalState.actions.addToastStart('Unverified Company created successfully!', ToastType.SUCCESS))
      ).pipe(handleToastError(GENERAL.LOADING_KEY.ADD_UNVERIFIED_COMPANIES_START, ['name', 'taxCondition']))
    )
  );

export const updateUnverifiedCompanySponsorStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.UPDATE_UNVERIFIED_COMPANY_SPONSOR_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_UNVERIFIED_COMPANY_SPONSOR, true)),
        deps.apiService.updateUnverifiedCompanySponsor(payload.clientId, payload.projectId, payload.sponsorId).pipe(
          mergeMap(res => {
            return [];
          })
        ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_UNVERIFIED_COMPANY_SPONSOR, false)),
        of(generalState.actions.addToastStart('Sponsor updated successfully!', ToastType.SUCCESS))
      ).pipe(handleToastError(GENERAL.LOADING_KEY.UPDATE_UNVERIFIED_COMPANY_SPONSOR))
    )
  );

export const updatePendingWelcomeClientNotesStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.UPDATE_PENDING_WELCOME_CLIENT_NOTES_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_PENDING_WELCOME_CLIENT_NOTES, true)),
        deps.apiService
          .updatePendingWelcomeClientNotes(payload)
          .pipe(
            map(res => actions.fetchClientListStart(payload.queryParamsForFetchingList))
          ),
        of(generalState.actions.addToastStart(`Notes updated successfully!`, ToastType.SUCCESS)),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_PENDING_WELCOME_CLIENT_NOTES, false))
      ).pipe(handleToastError(GENERAL.LOADING_KEY.UPDATE_PENDING_WELCOME_CLIENT_NOTES))
    )
  );

export const epics = [
  fetchClientStart,
  fetchSelfCompanyStart,
  fetchDraftClientStart,
  fetchClientHierarchyStart,
  updateClientHierarchyStart,
  fetchUserClientsStart,
  unmarkBranchStart,
  fetchClientBranchesStart,
  inviteCompanyAsABranchStart,
  fetchOnboardingClientStart,
  fetchClientListStart,
  fetchClientSummaryStart,
  fetchClientDraftSummaryStart,
  fetchMwbeTypesStart,
  fetchTradesStart,
  fetchProjectClientListStart,
  fetchSubContractorListStart,
  fetchUserClientListStart,
  fetchProjectClientHirearchyListStart,
  saveClientStart,
  inviteClientStart,
  updateDraftClientStart,
  sendApproveClientStart,
  sendApproveOnboardingClientStart,
  approveClientStart,
  approveOnboardingClientStart,
  searchClientStart,
  searchInviteClientStart,
  deleteClientStart,
  updateClientStart,
  updateOnboardingClientStart,
  archiveClientStart,
  unarchiveClientStart,
  fetchProjectClientSummaryStart,
  updateProjectClientTaxConditionStart,
  updateWelcomeKitStart,
  fetchAdminPermissionStart,
  deleteOnboardingClientStart,
  fetchProjectCompanyStart,
  updateProjectCompanyStart,
  fetchJointVentureCompanyUsersStart,
  assignClientProjectStart,
  fetchFailedTransactionsStart,
  fetchMergeCompanyUserStart,
  fetchMergeCompanySummaryStart,
  fetchAllActiveClientsStart,
  fetchMergeCompanyInvoicesStart,
  fetchMergeCompanyInvoicesSummaryStart,
  fetchMergeCompanyWorkersStart,
  fetchMergeCompanyBillingSummaryStart,
  fetchMergeCompanyProjectSummaryStart,
  saveMergeCompanyStart,
  fetchMergeCompanyReviewInvoicesSummaryStart,
  fetchClientListForMultipleProjectsStart,
  addUnverifiedCompaniesStart,
  updateUnverifiedCompanySponsorStart,
  updatePendingWelcomeClientNotesStart,
];
